/* eslint-disable prefer-rest-params */
/* eslint-disable func-names */
import { ANALYTICS, PAST_VISIT_DISPLAY_TIME_WINDOW } from '../../config';
import logger from '../../core/logger';
import { isEmptyArray } from '../util/array';
import { isClientSide } from '../util/system';
import DateTime, { YEAR_MONTH_DAY_DASHED } from '../../components/DateTime';
import { getAgeFromBirthDate } from '../util/date';
import { isEmptyString } from '../util/string';
import { hoursSinceNow } from '../util/time';
import {
  getNativeAppVersionInUse,
  getNativeBuildNumber,
  getNativeCarrier,
  getNativeDeviceId,
  getNativeDeviceLocale,
  getNativeSystemVersion,
  isAndroidApp,
  isAndroidPwa,
  isIosApp,
  isIosPwa,
  isNativeApp,
} from '../util/device';
import { getIsMixpanelAliased, setIsMixpanelAliased, setIsNativeAppUser } from '../dapi/tracking';
import { isPersistedLogin } from '../session';
import { safeGet } from '../util/object';
import { analyticsTrackEventOnlyToIterableViaSegment } from './iterable';

const analyticsTrackPage = (category, name, location) => {
  if (isClientSide() && ANALYTICS.enabled) {
    if (!window.analytics) {
      logger.warn('Track page Analytics not loaded');
      return;
    }

    // SOLV-2030: page title is passed automatically, url and path needs to be hardcoded because of
    // our use of canonicals, see docs
    // So no need to override , see https://segment.com/docs/sources/website/analytics.js/#page

    window.analytics.page(category, name, {
      isIosPwa: isIosPwa(),
      isAndroidPwa: isAndroidPwa(),
      isIosApp: isIosApp(),
      isAndroidApp: isAndroidApp(),
      isNativeApp: isNativeApp(),
      url: window.location.href,
      path: window.location.pathname,
    });
  }
};

export const getExtendedProperties = (properties, originalEventName) => ({
  ...properties,
  url: window.location.href,
  path: window.location.pathname,
  isIosPwa: isIosPwa(),
  isAndroidPwa: isAndroidPwa(),
  isIosApp: isIosApp(),
  isAndroidApp: isAndroidApp(),
  isNativeApp: isNativeApp(),
  originalEventName,
});

// facebook app events only allow alphanumeric even names shorter than 40 chars
const getEventNameForFacebookApps = eventName =>
  eventName.replace(/[^a-z0-9]/gim, ' ').substring(0, 40);

const analyticsTrackEvent = (event, properties) => {
  if (isClientSide() && ANALYTICS.enabled) {
    // Segment
    if (window.analytics) {
      // Send the event to all the destinations besides Iterable, which has a custom handling.
      // see analyticsTrackingEventOnlyToIterableViaSegment below
      const segmentOptions = {
        integrations: {
          All: true,
          Iterable: false,
        },
      };
      window.analytics.track(event, getExtendedProperties(properties, event), segmentOptions);
      analyticsTrackEventOnlyToIterableViaSegment(event, properties);
    } else {
      logger.warn('Track event Analytics not loaded');
      return;
    }

    // Facebook app
    if (window.FB) {
      const facebookEventName = getEventNameForFacebookApps(event);
      try {
        window.FB.AppEvents.logEvent(
          facebookEventName,
          null,
          getExtendedProperties(properties, event)
        );
      } catch (e) {
        console.warn('Facebook app analytics dropped an event');
      }
    } else {
      logger.warn('Facebook app analytics not loaded');
    }
  }
};

const analyticsTrackFormErrors = (event, errors) => {
  const properties = {
    ...errors,
    fields: Object.keys(errors).join(','),
    url: window.location.href,
    path: window.location.pathname,
    isIosPwa: isIosPwa(),
    isAndroidPwa: isAndroidPwa(),
    isIosApp: isIosApp(),
    isAndroidApp: isAndroidApp(),
    isNativeApp: isNativeApp(),
  };
  analyticsTrackEvent(event, properties);
};

const analyticsIdentify = async (accountId, bookingPostData) => {
  if (isClientSide() && ANALYTICS.enabled) {
    if (!window.analytics) {
      logger.warn('Analytics not loaded');
      return;
    }

    const isUserAlreadyTracked = await getIsMixpanelAliased(accountId);
    if (!isUserAlreadyTracked) {
      window.analytics.alias(accountId);
      setIsMixpanelAliased(accountId);
    }

    const identifyTraits = {
      origin: bookingPostData.booking.origin,
      reason: bookingPostData.booking.reason_for_visit,
      insurer_type: bookingPostData.insurance_profile.insurer_type,
      insurer_code: bookingPostData.insurance_profile.insurer_code,
      url: window.location.href,
      path: window.location.pathname,
      id: accountId,
      isIosPwa: isIosPwa(),
      isAndroidPwa: isAndroidPwa(),
      isIosApp: isIosApp(),
      isAndroidApp: isAndroidApp(),
      isNativeApp: isNativeApp(),
    };

    if (safeGet(bookingPostData)('booking.email')) {
      identifyTraits.email = safeGet(bookingPostData)('booking.email');
    }
    window.analytics.identify(accountId, identifyTraits);
  }
};

const analyticsLoginIdentify = async accountSummary => {
  if (isClientSide() && ANALYTICS.enabled) {
    if (!window.analytics) {
      logger.warn('Analytics not loaded');
      return;
    }

    if (!accountSummary || isEmptyArray(accountSummary.user_profiles)) {
      logger.warn('No account summary found');
      return;
    }

    const accountId = accountSummary.id;

    const userProfiles = accountSummary.user_profiles;
    const oldestProfile = userProfiles
      .filter(p => p.birth_date)
      .sort((a, b) => getAgeFromBirthDate(b.birth_date) - getAgeFromBirthDate(a.birth_date))
      .shift();
    const youngestProfile = userProfiles
      .filter(p => p.birth_date)
      .sort((a, b) => getAgeFromBirthDate(a.birth_date) - getAgeFromBirthDate(b.birth_date))
      .shift();
    const lastBooking = accountSummary.bookings.most_recent;

    const properties = {
      market: accountSummary.market,
      isIosPwa: isIosPwa(),
      isAndroidPwa: isAndroidPwa(),
      isPersistedLogin: isPersistedLogin(),

      accountCreatedDate: DateTime.format(accountSummary.created_date)(YEAR_MONTH_DAY_DASHED),
      accountOnboardingDate: DateTime.format(accountSummary.onboarding_date)(YEAR_MONTH_DAY_DASHED),
      accountLoginCount: accountSummary.login_count,
      accountFirstLogin: DateTime.format(accountSummary.first_login)(YEAR_MONTH_DAY_DASHED),
      accountLastLogin: DateTime.format(accountSummary.last_login)(YEAR_MONTH_DAY_DASHED),
      accountSolvUserDate: DateTime.format(accountSummary.solv_user_date)(YEAR_MONTH_DAY_DASHED),
      accountProfiles: userProfiles.length,
      accountUnverifiedProfiles: userProfiles.filter(p => !p.is_verified).length,
      accountUnder4Profiles: userProfiles.filter(
        p => p.birth_date && getAgeFromBirthDate(p.birth_date) < 4
      ).length,
      accountBetween4and18Profiles: userProfiles.filter(
        p =>
          p.birth_date &&
          getAgeFromBirthDate(p.birth_date) >= 4 &&
          getAgeFromBirthDate(p.birth_date) <= 18
      ).length,
      accountOver18Profiles: userProfiles.filter(
        p => p.birth_date && getAgeFromBirthDate(p.birth_date) > 18
      ).length,
      accountOldestProfileAge: getAgeFromBirthDate(safeGet(oldestProfile)('birth_date')),
      accountYoungestProfileAge: getAgeFromBirthDate(safeGet(youngestProfile)('birth_date')),
      accountTotalBookings: accountSummary.bookings.total,
      accountActiveInsuranceProfiles: accountSummary.insurance_profiles.filter(ip => ip.active)
        .length,
      accountInsuranceCarrier: accountSummary.insurance_profiles.length
        ? accountSummary.insurance_profiles[0].insurer_name
        : null,
      accountLastBookingDate:
        lastBooking && DateTime.format(lastBooking.appointment_date)(YEAR_MONTH_DAY_DASHED),
      accountLastBookingOrigin: lastBooking && lastBooking.origin,
      accountLastBookingIsTelemed: lastBooking && !!lastBooking.telemed_status,
      accountLastBookingStatus: lastBooking && lastBooking.status,
      accountLastBookingInVisit:
        lastBooking &&
        hoursSinceNow(lastBooking.appointment_date) > -1 * PAST_VISIT_DISPLAY_TIME_WINDOW,
    };

    if (!isEmptyString(safeGet(accountSummary, '')('email'))) {
      properties.email = accountSummary.email;
    }

    if (isNativeApp()) {
      const nativeProperties = {
        appVersion: getNativeAppVersionInUse(),
        systemVersion: getNativeSystemVersion(),
        buildNumber: getNativeBuildNumber(),
        deviceId: getNativeDeviceId(),
        deviceLocale: getNativeDeviceLocale(),
        carrier: getNativeCarrier(),
      };

      Object.assign(properties, nativeProperties);

      // Make sure that the related account is tagged to the proper device'
      if (
        (!accountSummary.is_ios_app_user && isIosApp()) ||
        (!accountSummary.is_android_app_user && isAndroidApp())
      ) {
        setIsNativeAppUser(accountId);
      }
    }

    const isUserAlreadyTracked = accountSummary.is_mixpanel_aliased;
    if (!isUserAlreadyTracked) {
      window.analytics.alias(accountId);
      setIsMixpanelAliased(accountId);
    }

    window.analytics.identify(accountId, properties);
  }
};

const analyticsTagHotjarRecording = tags => {
  if (isClientSide() && ANALYTICS.enabled && !isEmptyArray(tags)) {
    if (!window.hj) {
      window.hj =
        window.hj ||
        function() {
          (hj.q = hj.q || []).push(arguments);
        };
      if (!window.hj) {
        logger.warn('Hot jar tagging not loaded');
        return;
      }
    }

    hj('tagRecording', tags);
  }
};

const analyticsTriggerHotjarRecording = recordingTag => {
  if (isClientSide() && ANALYTICS.enabled && !isEmptyString(recordingTag)) {
    if (!window.hj) {
      window.hj =
        window.hj ||
        function() {
          (hj.q = hj.q || []).push(arguments);
        };
      if (!window.hj) {
        logger.warn('Hot jar trigger recording not loaded');
        return;
      }
    }

    hj('trigger', recordingTag);
  }
};

export {
  analyticsTrackPage,
  analyticsTrackEvent,
  analyticsLoginIdentify,
  analyticsIdentify,
  analyticsTrackFormErrors,
  analyticsTagHotjarRecording,
  analyticsTriggerHotjarRecording,
};
